import styled from 'styled-components';

export const Wrapper = styled.section`
  position: relative;
  background: var(--night);
  color: var(--on-night);
  isolation: isolate;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -2;
    background: ${({ $backdrop }) => ($backdrop ? `url(${$backdrop})` : 'none')}
      center 20% / cover no-repeat;
    opacity: 0.55;
    animation: fadeIn 1.2s var(--ease);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(
        90deg,
        rgba(18, 15, 12, 0.97) 0%,
        rgba(18, 15, 12, 0.82) 45%,
        rgba(18, 15, 12, 0.45) 100%
      ),
      linear-gradient(0deg, var(--night) 0%, rgba(18, 15, 12, 0) 40%);
  }
`;

export const Content = styled.div`
  max-width: var(--maxWidth);
  margin: 0 auto;
  padding: 28px var(--gutter) clamp(48px, 7vw, 88px);

  .layout {
    display: grid;
    grid-template-columns: minmax(220px, 340px) 1fr;
    gap: clamp(28px, 5vw, 64px);
    align-items: start;
    margin-top: 32px;

    @media (max-width: 820px) {
      grid-template-columns: 1fr;
    }
  }
`;

export const Crumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.04em;
  color: var(--on-night-muted);
  min-width: 0;

  a {
    flex-shrink: 0;
    display: inline-flex;
    gap: 8px;
    padding: 8px 14px;
    border: 1px solid rgba(246, 241, 232, 0.18);
    border-radius: 999px;
    color: var(--on-night);
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: var(--on-night);
      color: var(--ink);
    }
  }

  .current {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const Poster = styled.div`
  position: relative;
  animation: fadeUp 0.7s var(--ease) both;

  img,
  .fallback {
    width: 100%;
    aspect-ratio: 2 / 3;
    object-fit: cover;
    border-radius: 18px;
    box-shadow: 0 40px 80px -24px rgba(0, 0, 0, 0.8),
      0 0 0 1px rgba(246, 241, 232, 0.08);
  }

  .fallback {
    display: grid;
    place-items: center;
    background: var(--night-2);
    font-family: var(--font-display);
    font-style: italic;
    font-weight: 800;
    font-size: 96px;
    color: var(--accent);
  }

  @media (max-width: 820px) {
    max-width: 240px;
  }
`;

export const Text = styled.div`
  animation: fadeUp 0.7s 0.1s var(--ease) both;

  .kicker {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.08em;

    span {
      padding: 5px 10px;
      border: 1px solid rgba(246, 241, 232, 0.18);
      border-radius: 6px;
    }

    .status {
      border-color: var(--gold);
      color: var(--gold);
    }
  }

  h1 {
    margin-top: 20px;
    font-size: clamp(40px, 6vw, 80px);
    font-weight: 800;
    line-height: 0.95;
    letter-spacing: -0.035em;
    overflow-wrap: anywhere;
  }

  .original {
    margin-top: 10px;
    font-family: var(--font-display);
    font-style: italic;
    color: var(--on-night-muted);
  }

  .tagline {
    margin-top: 16px;
    font-family: var(--font-display);
    font-style: italic;
    font-size: clamp(18px, 2vw, 22px);
    color: var(--gold);
  }

  .genres {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 24px 0 0;
    padding: 0;
    list-style: none;

    li {
      padding: 6px 14px;
      border-radius: 999px;
      background: rgba(246, 241, 232, 0.1);
      font-size: 13px;
      font-weight: 500;
    }
  }

  .overview {
    max-width: 680px;
    margin-top: 32px;

    h2 {
      margin-bottom: 10px;
      font-family: var(--font-mono);
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--on-night-muted);
    }

    p {
      font-size: 17px;
      line-height: 1.65;
      color: rgba(246, 241, 232, 0.9);
    }
  }

  .bottom {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 28px 48px;
    margin-top: 36px;
    padding-top: 28px;
    border-top: 1px solid rgba(246, 241, 232, 0.12);
  }

  dl {
    display: flex;
    flex-wrap: wrap;
    gap: 20px 40px;
    margin: 0;
  }

  dt {
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--on-night-muted);
  }

  dd {
    margin: 4px 0 0;
    font-family: var(--font-display);
    font-size: 20px;
    font-weight: 600;
  }

  .links {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 32px;

    a {
      padding: 10px 18px;
      border-radius: 999px;
      border: 1px solid rgba(246, 241, 232, 0.2);
      font-size: 14px;
      font-weight: 500;
      transition: background 0.2s, color 0.2s, border-color 0.2s;

      &:first-child {
        background: var(--accent);
        border-color: var(--accent);
        color: #fff;
      }

      &:hover {
        background: var(--on-night);
        border-color: var(--on-night);
        color: var(--ink);
      }
    }
  }
`;
